import { useEffect, useRef, useState } from 'react'

import { ErrorLabel } from 'common/components/ErrorLabel/ErrorLabel'
import { CodeSelect } from 'features/Procedure/features/treatments/components/code-select/code-select'
import type { EmrCodeItem } from 'features/Procedure/state/procedure-api.type'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'
import { SERIES_PAGES_TEXT } from 'features/Series/pages/shared/seriesPagesText'

import styles from './templateFields.module.scss'

export type TemplateBillingValue = { type: string; code: string } | null

type Props = {
  value: TemplateBillingValue
  onChange: (value: TemplateBillingValue) => void
  disabled?: boolean
  error?: string
}

const TEXT = SERIES_PAGES_TEXT.modal

// Procedure code search by code type only: needs no patient or appointment.
export const TemplateBillingField = ({ value, onChange, disabled = false, error }: Props) => {
  const [picking, setPicking] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  // CodeSelect has no autoFocus prop, so focus its input once the search opens.
  useEffect(() => {
    if (picking) searchRef.current?.querySelector('input')?.focus()
  }, [picking])

  const handlePick = (picked: EmrCodeItem[] | EmrCodeItem | null) => {
    if (!picked || Array.isArray(picked)) return
    onChange({ type: picked.code_type, code: picked.code.replace(/\s+/g, '') })
    setPicking(false)
  }

  return (
    <div className={styles.field}>
      {value ? (
        <div className={styles.row}>
          <span className={styles.billingCode}>{`${value.type}:${value.code}`}</span>
          {!disabled && (
            <button
              type='button'
              className={styles.remove}
              aria-label='Remove billing code'
              onClick={() => onChange(null)}>
              ×
            </button>
          )}
        </div>
      ) : picking ? (
        <div className={styles.row}>
          <div className={styles.grow} ref={searchRef}>
            <CodeSelect
              className={styles.codeSearch}
              codeType={ProcedureTypeCode.CPT4}
              onChange={handlePick}
              value={null}
              disabled={disabled}
              error={error}
            />
          </div>
          <button
            type='button'
            className={styles.link}
            disabled={disabled}
            onClick={() => setPicking(false)}>
            {TEXT.cancel}
          </button>
        </div>
      ) : (
        <div className={styles.row}>
          <span className={styles.none}>{TEXT.billingNone}</span>
          <button
            type='button'
            className={`${styles.link} ${styles.pick}`}
            disabled={disabled}
            onClick={() => setPicking(true)}>
            {TEXT.pickCode}
          </button>
        </div>
      )}
      <span className={styles.hint}>{TEXT.billingHint}</span>
      {!picking && <ErrorLabel error={error} />}
    </div>
  )
}
